import { UserIcon, TrashIcon, PencilIcon } from "@heroicons/react/solid";
import { Button } from "../../components/ui";
import UserManagementModal from "./UserManagementModal";
import AddLocationModal from "./AddLocationModal";
import DeleteConfirmationModal from "../../components/ui/DeleteConfirmationModal";
import useGeofencing from "../../hooks/geofencing/useGeofencing";
import Pagination from "../../components/pagination";
import { PrimaryLoader, InlineLoader } from "../../components/Loader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";
import RoundedContainer from "../../components/containers/RoundedContainer";
import SearchBar from "../../components/inputs/SearchBar";

const Geofencing = () => {
  // Use our custom hook for API interactions
  const {
    pageNo,
    searchTerm,
    isLoading,
    addLocationModal,
    selectedBranchId,
    userManagementModalOpen,
    deleteConfirmationModal,
    geofenceList,
    singleGeofenceData,
    isSingleGeofenceLoading,
    isEditing,
    isDeleting,
    isSubmitting,
    isMapping,
    isUsersLoading,
    isAvailableUsersLoading,
    isAddedUsersLoading,
    limit,
    handleSearch,
    handlePageChange,
    handleEditLocation,
    handleAddLocation,
    handleSubmitLocation,
    handleOpenUserManagement,
    handleOpenDeleteModal,
    handleDeleteLocation,
    handleAddUser,
    handleRemoveUser,
    handleSaveUsers,
    availableUsers,
    addedUsers,
    setAddLocationModal,
    setUserManagementModalOpen,
    setDeleteConfirmationModal,
    handleAvailableUsersSearch,
    handleAddedUsersSearch,
    availableUsersSearchQuery,
    addedUsersSearchQuery,
    loadMoreAvailableUsers,
    loadMoreAddedUsers,
    isFetchingAvailableUsers,
    isFetchingAddedUsers,
    hasMoreAvailableUsers,
    hasMoreAddedUsers,
  } = useGeofencing();

  return (
    <div className="py-10 px-4">
      <div>
        <div className="flex justify-between mb-5">
          <h1 className="text-2xl font-bold">Geofencing</h1>
        </div>

        {/* Search bar */}
        <RoundedContainer className={"flex justify-between items-center"}>
          <div className="w-[400px]">
            <SearchBar
              value={searchTerm}
              disabled={isLoading}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          <Button
            onClick={handleAddLocation}
            disabled={isLoading || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <InlineLoader className="text-white mr-2" />
                Adding...
              </>
            ) : (
              "Add Location"
            )}
          </Button>
        </RoundedContainer>

        <div className="max-w-full">
          {isLoading ? (
            <PrimaryLoader />
          ) : geofenceList?.data?.length === 0 ? (
            <div className="text-center py-8">No geofence locations found</div>
          ) : (
            <>
              <RoundedContainer>
                <Table className="table-auto w-full rounded-md">
                  <TableHead>
                    <TableHeaderCell className="text-sm font-semibold">
                      Sr No
                    </TableHeaderCell>
                    <TableHeaderCell className="text-sm font-semibold">
                      Name
                    </TableHeaderCell>
                    <TableHeaderCell className="text-sm font-semibold">
                      Address
                    </TableHeaderCell>
                    <TableHeaderCell className="text-sm font-semibold">
                      Latitude
                    </TableHeaderCell>
                    <TableHeaderCell className="text-sm font-semibold">
                      Longitude
                    </TableHeaderCell>
                    <TableHeaderCell className="text-sm font-semibold">
                      Radius (m)
                    </TableHeaderCell>
                    <TableHeaderCell className="text-sm font-semibold">
                      Manage Users
                    </TableHeaderCell>
                    <TableHeaderCell className="text-sm font-semibold">
                      Actions
                    </TableHeaderCell>
                  </TableHead>
                  <TableBody>
                    {geofenceList?.data?.map((data, index) => (
                      <TableRow key={data.branch_id}>
                        <TableCell className="text-center text-dark font-medium text-sm">
                          {pageNo * limit + index + 1}
                        </TableCell>
                        <TableCell className="text-center text-dark font-medium text-sm py-2">
                          {data.name}
                        </TableCell>
                        <TableCell className="text-center text-dark font-medium text-sm py-2">
                          {data.address_1 || "N/A"}
                        </TableCell>
                        <TableCell className="text-center text-dark font-medium text-sm py-2">
                          {data.lat}
                        </TableCell>
                        <TableCell className="text-center text-dark font-medium text-sm py-2">
                          {data.long}
                        </TableCell>
                        <TableCell className="text-center text-dark font-medium text-sm py-2">
                          {data.radius}
                        </TableCell>
                        <TableCell className="text-center text-dark font-medium text-sm py-2">
                          <div className="flex items-center justify-center">
                            <Button
                              onClick={() =>
                                handleOpenUserManagement(data.branch_id)
                              }
                              className="flex items-center space-x-1"
                              disabled={isSubmitting || isDeleting}
                            >
                              <UserIcon className="w-4 h-4 mr-1" />
                              <span>Manage Users</span>
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-center text-dark font-medium text-sm">
                          <div className="flex items-center justify-center space-x-2">
                            <Button
                              variant="ghost"
                              onClick={() => handleEditLocation(data.branch_id)}
                              className="text-blue-600"
                              disabled={isSubmitting || isDeleting}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() =>
                                handleOpenDeleteModal(data.branch_id)
                              }
                              className="text-red-600"
                              disabled={isSubmitting || isDeleting}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </RoundedContainer>

              {/* Pagination - using the Pagination component */}
              {geofenceList?.total_count > 0 && (
                <RoundedContainer>
                  <Pagination
                    lengthofItems={geofenceList.total_count}
                    onPageChange={handlePageChange}
                    limit={limit}
                    currentPage={pageNo}
                  />
                </RoundedContainer>
              )}
            </>
          )}
        </div>
      </div>

      {/* User Management Modal Component (now connected to APIs) */}
      <UserManagementModal
        open={userManagementModalOpen}
        onClose={() => setUserManagementModalOpen(false)}
        geofenceName={
          selectedBranchId &&
          geofenceList?.data?.find((g) => g.branch_id === selectedBranchId)
            ?.name
        }
        availableUsers={availableUsers}
        addedUsers={addedUsers}
        onAddUser={handleAddUser}
        onRemoveUser={handleRemoveUser}
        onSave={handleSaveUsers}
        onAvailableUsersSearch={handleAvailableUsersSearch}
        onAddedUsersSearch={handleAddedUsersSearch}
        loadMoreAvailableUsers={loadMoreAvailableUsers}
        loadMoreAddedUsers={loadMoreAddedUsers}
        availableUsersSearchQuery={availableUsersSearchQuery}
        addedUsersSearchQuery={addedUsersSearchQuery}
        isLoading={isUsersLoading}
        isAvailableUsersLoading={isAvailableUsersLoading}
        isAddedUsersLoading={isAddedUsersLoading}
        isFetchingAvailableUsers={isFetchingAvailableUsers}
        isFetchingAddedUsers={isFetchingAddedUsers}
        hasMoreAvailableUsers={hasMoreAvailableUsers}
        hasMoreAddedUsers={hasMoreAddedUsers}
        isSubmitting={isMapping}
      />

      {/* Add/Edit Location Modal Component */}
      <AddLocationModal
        open={addLocationModal}
        onClose={() => setAddLocationModal(false)}
        onSave={handleSubmitLocation}
        isEditing={isEditing}
        initialData={isEditing ? singleGeofenceData : null}
        isLoading={isSingleGeofenceLoading}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteConfirmationModal}
        onClose={() => setDeleteConfirmationModal(false)}
        onDelete={handleDeleteLocation}
        title="Delete Geofence Location"
        message="Are you sure you want to delete this geofence location? This action cannot be undone."
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Geofencing;
